import request from './common/request'

import { SIMBART_MERCHANT_DISPLAY_WALL_URL, MOBILE_SIMBART_PUBLIC_URL } from './urls'

/**
 * 查询已配置的展示墙赛程列表
 * @param {Number|String} competitionId - 活动ID
 * @returns {Promise} 返回 DisplayWallStageConfigDto[] 数组
 */
async function getStages(competitionId) {
  return request({
    url: `${SIMBART_MERCHANT_DISPLAY_WALL_URL}/stages`,
    method: 'GET',
    params: {
      competitionId
    }
  })
}

/**
 * 保存展示墙赛程范围
 * @param {Object} data - 请求数据
 * @param {Number|String} data.competitionId - 活动ID
 * @param {Array<Number|String>} data.stageIds - 参与展示的赛程ID列表，空数组表示清空
 * @returns {Promise} 返回保存结果
 */
async function saveStages(data) {
  return request({
    url: `${SIMBART_MERCHANT_DISPLAY_WALL_URL}/stages`,
    method: 'POST',
    data: data // JSON 格式，直接传递对象
  })
}

/**
 * 获取展示墙报名表列表（含置顶字段）
 * @param {Object} params - 请求参数
 * @param {Number|String} params.competitionId - 活动ID（必传）
 * @param {Array<Number|String>} [params.stageIds] - 赛程ID列表（多选），当传了 stageIds 时，stageId 不生效
 * @param {Number|String} [params.stageId] - 赛程ID（单选），当 stageIds 不传时生效
 * @param {String} [params.competitorName] - 报名姓名（可选）
 * @param {String} [params.competitorPhone] - 报名手机（可选）
 * @param {String} [params.ticket] - 报名编号（可选）
 * @param {Number|String} [params.entryValueId] - 报名表ID（可选，用于通过报名表ID搜索，当输入纯数字时与 ticket 同时传递）
 * @param {Number|String} [params.matchId] - 分类组别ID（可选）
 * @param {Number} [params.hasPinConfig] - 是否配置了置顶，0=未配置，1=已配置（可选）
 * @param {Number} [params.page] - 页码，默认1
 * @param {Number} [params.pageSize] - 每页条数，默认10
 * @returns {Promise} 返回分页对象，data.data 为 DisplayWallEntryWithPinDto[]
 */
async function getEntryPinList(params) {
  // 自定义参数序列化：将数组参数序列化为 key=value1&key=value2 格式（不带方括号）
  const paramsSerializer = (params) => {
    const parts = []
    for (const key in params) {
      if (params.hasOwnProperty(key)) {
        const value = params[key]
        if (value === null || value === undefined) {
          continue
        }
        if (Array.isArray(value)) {
          // 数组参数：重复键名，不带方括号
          value.forEach(item => {
            if (item !== null && item !== undefined) {
              parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(item)}`)
            }
          })
        } else {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
        }
      }
    }
    return parts.join('&')
  }
  
  return request({
    url: `${SIMBART_MERCHANT_DISPLAY_WALL_URL}/entryPin/list`,
    method: 'GET',
    params: params,
    paramsSerializer: paramsSerializer
  })
}

/**
 * 设置/取消单条置顶
 * @param {Object} data - 请求数据
 * @param {Number|String} data.competitionId - 活动ID（必传）
 * @param {Number|String} data.entryValueId - 报名表ID（必传）
 * @param {Number} data.isTop - 是否置顶，1=置顶，0=取消置顶（必传）
 * @param {Number} [data.sort] - 置顶排序，数字越大越靠前，置顶时必传，取消置顶时可忽略
 * @param {String} [data.remark] - 置顶备注（可选）
 * @param {String} [data.pinRemark] - 置顶备注（可选，与 remark 等价）
 * @returns {Promise} 返回保存结果
 */
async function setEntryPin(data) {
  // 构建请求数据
  const requestData = {
    competitionId: data.competitionId,
    entryValueId: data.entryValueId,
    isTop: data.isTop,
    sort: data.sort
  }
  
  // 处理备注字段：如果传入了 remark，转换为 pinRemark（接口字段名）
  // 优先使用 remark，如果没有则使用 pinRemark
  if (data.remark !== undefined && data.remark !== null && data.remark !== '') {
    requestData.pinRemark = data.remark
  } else if (data.pinRemark !== undefined && data.pinRemark !== null && data.pinRemark !== '') {
    requestData.pinRemark = data.pinRemark
  }
  
  return request({
    url: `${SIMBART_MERCHANT_DISPLAY_WALL_URL}/entryPin`,
    method: 'PUT',
    data: requestData // JSON 格式，直接传递对象
  })
}

/**
 * 批量设置置顶顺序
 * @param {Object} data - 请求数据
 * @param {Number|String} data.competitionId - 活动ID（必传）
 * @param {Array<Object>} [data.items] - 置顶项列表（可选）
 * @param {Number|String} data.items[].entryValueId - 报名表ID（必传）
 * @param {Number} [data.items[].isTop] - 是否置顶，1=置顶，0=取消置顶（可选）
 * @returns {Promise} 返回保存结果
 */
async function batchSetEntryPin(data) {
  return request({
    url: `${SIMBART_MERCHANT_DISPLAY_WALL_URL}/entryPin/batch`,
    method: 'PUT',
    data: data // JSON 格式，直接传递对象
  })
}

/**
 * 公开报名列表（展示墙扩展）- 移动端公开接口
 * @param {Object} params - 请求参数
 * @param {String} params.merchantId - 商户ID（必传）
 * @param {Number|String} [params.competitionId] - 活动ID（展示墙场景建议）
 * @param {Number|String} [params.stageId] - 赛程ID（必须在展示墙已配置）
 * @param {Number|String} [params.categoryId] - 分类ID（展示墙筛选）
 * @param {Number|String} [params.groupId] - 分组ID（展示墙筛选）
 * @param {String} [params.competitorName] - 参赛者姓名（模糊搜索）
 * @param {Number|String} [params.matchId] - 分赛ID
 * @param {Number|String} [params.sessionId] - 场次ID
 * @param {Number} [params.promotionStatus] - 晋级状态：1=待处理，2=...
 * @param {Number} [params.page] - 页码
 * @param {Number} [params.pageSize] - 每页条数
 * @returns {Promise} 返回分页对象，结果按置顶序号降序 → 置顶降序 → 其他排序
 */
async function getPublicEntryValues(params) {
  return request({
    url: `${MOBILE_SIMBART_PUBLIC_URL}/entry_values`,
    method: 'GET',
    params: params
  })
}

export default {
  getStages,
  saveStages,
  getEntryPinList,
  setEntryPin,
  batchSetEntryPin,
  getPublicEntryValues
}



// WEBPACK FOOTER //
// ./src/api/merchant-simbart-display-wall.js